<template>
  <main role="main" class="py-4">
    <b-container>

      <div v-if="isLoading" class="py-5 text-center"><b-spinner/></div>

      <template v-else-if="meeting">
        <div class="d-flex flex-row align-items-start justify-content-between">
          <div>
            <label class="text-secondary">{{ $t("meetingDetails") }}</label>
            <h5>{{ meeting.name }}</h5>
            <div v-if="isUserAdmin()">
              {{ $t("customer") }}:
              <router-link :to="`/customers/details/` + meeting.customerId">
                {{ meeting.customerName || meeting.customerId }}
              </router-link>
            </div>
          </div>
          <div class="text-right flex-shrink-0">
            <div><span class="text-secondary">{{ $t("meetingId") }}: </span> {{ meeting.id }}</div>
            <div>
              <span class="text-secondary">{{ $t("status") }}: </span>
              <b-badge :variant="statusBadgeVariant[meeting.status]">{{ statusText[meeting.status] }}</b-badge>
            </div>
            <div>
              <b-button v-if="!isViewUser() && ['INPROGRESS', 'END'].includes(meeting.status)" class="mx-2" variant="link" size="sm" :to="`/monitor-meeting/${meeting.id}`"
                    v-b-tooltip.hover :title="$t('viewStats')">
              <b-icon-graph-up></b-icon-graph-up>
            </b-button>
            </div>
          </div>
        </div>
        <div class="d-flex flex-row align-items-center justify-content-between mt-2 mb-3">
          <div>
            <b-button variant="outline-primary" size="sm" class="mr-4" v-if="meeting.owner && ['PENDING', 'END'].includes(meeting.status)" @click="onClickStartMeeting">
              <b-icon icon="display" class="mr-1"></b-icon> {{ $t("start") }}
            </b-button>

            <template v-if="meeting.status === 'INPROGRESS'">
              <b-button v-if="meeting.owner || meeting.canJoin" variant="outline-primary" size="sm" class="mr-4" @click="onClickJoinMeeting">
                <b-icon icon="display" class="mr-1"></b-icon> {{ $t("joinMeeting") }}
              </b-button>
              <b-button v-if="meeting.owner" variant="outline-danger" size="sm" @click="onClickEndMeeting">
                <b-icon icon="power" class="mr-1"></b-icon> {{ $t("endMeeting") }}
              </b-button>
            </template>

            <template v-if="meeting.status === 'END' && (meeting.owner || isUserAdmin())">
              <b-spinner v-if="isLoadingRecordUrl" small />
              <b-button v-else-if="playbackUrl" variant="outline-primary" size="sm" :href="playbackUrl" target="_blank">
                <b-icon icon="play-circle" class="mr-1"></b-icon> {{ $t("viewMeetingRecordedFile") }}
              </b-button>
              <span class="text-secondary" v-else>
                <b-icon icon="play-circle" class="mr-1"></b-icon> {{ $t("meetingNoRecordedFile") }}
              </span>
            </template>
          </div>
          <div v-if="meeting.owner || isUserAdmin()">
            <b-button variant="outline-secondary" size="sm" :to="`/meetings/new?cloneFrom=${meetingId}`">
              <b-icon-files class="mr-1" /> {{ $t("clone") }}
            </b-button>
            <b-button v-if="meeting.status == 'END' && peers.length !=0" variant="outline-primary" class="ml-4" size="sm"  @click="downloadFileParticipant">
              <b-icon-download class="mr-1" /> {{ $t("exportParticipants") }}
            </b-button>
            <b-button v-if="meeting.status == 'END' && contentRoom != ''" variant="outline-primary" class="ml-4" size="sm"  @click="downloadFileContentRoom">
              <b-icon-download class="mr-1" /> {{ $t("exportContentRoom") }}
            </b-button>
            <b-button v-if="meeting.status === 'PENDING'" variant="outline-primary" class="ml-4" size="sm"
                      :to="`/meetings/edit/${meetingId}`">
              <b-icon-pencil-square class="mr-1" /> {{ $t("updateInfo") }}
            </b-button>
            <b-button v-if="meeting.status === 'PENDING'" variant="outline-danger" class="ml-4" size="sm" @click="onClickCancelMeeting">
              {{ $t("cancelMeeting") }}
            </b-button>
            <b-button v-if="meeting.status !== 'INPROGRESS'" variant="outline-danger" class="ml-4" size="sm" @click="onClickDeleteMeeting">
              <b-icon-trash class="mr-1" /> {{ $t("deleteMeeting") }}
            </b-button>
          </div>
        </div>

        <b-alert v-if="emailStatusAlertText" show :variant="emailStatusAlertVariant" class="d-flex flex-row align-items-center justify-content-between">
          <div>
            <b-icon-exclamation-circle/> {{ emailStatusAlertText }}
          </div>
          <b-button v-if="meeting.owner && emailStatusButtonText" variant="outline-primary" size="sm" @click="onClickEmailStatusAlertButton">
            <b-icon-envelope class="mr-1" /> {{ emailStatusButtonText }}
          </b-button>
        </b-alert>

        <hr>

        <b-row id="conf-info-2">
          <b-col cols="5" class="border-right pr-5">
            <div class="d-flex flex-row align-items-center justify-content-between">
              <div class="text-secondary">{{ $t("meetingDate") }}</div>
              <div>{{ localDateFormat(meeting.scheduleStartTime) }}</div>
            </div>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <div class="text-secondary">{{ $t("meetingScheduledTime") }}</div>
              <div>{{ localTimeFormat(meeting.scheduleStartTime) }} - {{ localTimeFormat(meeting.scheduleEndTime) }}</div>
            </div>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <div class="text-secondary">{{ $t("meetingActualTime") }}</div>
              <div>{{ actualTimeStr }}</div>
            </div>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <div class="text-secondary">{{ $t("numOfParticipants") }}</div>
              <div>{{ meeting.numOfParticipants }}</div>
            </div>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <div class="text-secondary">{{ $t("TypeForm") }}</div>
              <div>{{ meeting.publicMeeting ? $t("public") : $t("private") }}</div>
            </div>
            <div v-if="meeting.activeFaceId" class="d-flex flex-row align-items-center justify-content-between">
              <div class="text-secondary">{{ $t("joinByFaceId") }}</div>
            </div>
            <hr>
            <!--<div class="d-flex flex-row align-items-center justify-content-between">
              <div class="text-secondary">Mật khẩu người điều hành</div>
              <div class="text-monospace">{{ meeting.operatorPassword }}</div>
            </div>-->
            <b-form-group label-for="input-join-url" v-if="meeting.owner || isUserAdmin()">
              <template v-slot:label><span class="text-secondary">{{ $t("hostJoinUrl") }}</span></template>
              <b-input-group size="sm">
                <b-form-input id="input-join-url" :value="meeting.hostJoinUrl" readonly/>
                <b-input-group-append>
                  <b-button variant="outline-secondary" @click="onClickCopyHostJoinUrl">
                    <b-icon-files />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
            <template v-if="meeting.owner || isUserAdmin()">
              <b-form-group label-for="input-join-url">
                <template v-slot:label><span class="text-secondary">{{ $t("participantJoinUrl") }}</span></template>
                <b-input-group>
                  <b-form-input id="input-join-url" size="sm" :value="meeting.participantJoinUrl" readonly/>
                  <b-input-group-append>
                    <b-button variant="outline-secondary" size="sm" @click="onClickCopyJoinUrl">
                      <b-icon-files />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
              <div v-if="participantJoinUrlHasAccessCode" class="d-flex flex-row align-items-center justify-content-between mb-2">
                <div class="text-secondary">{{ $t("accessCode") }}</div>
                <div class="text-monospace">{{ meeting.accessCode }}</div>
              </div>
              <div class="text-right" v-if="meeting.publicMeeting">
              <span class="text-primary cursor-pointer" @click="onClickCopyJoinUrlAndAccessCode">
                <b-icon-files class="mr-1" /> {{ $t("copy") }}
              </span>
            </div>
            <div class="text-right">
              <span class="text-primary cursor-pointer" @click="onClickOpenQRCodeModal">
                <b-icon-upc-scan class="mr-1" /> QRCODE
              </span>
            </div>
              <hr>
            </template>
            <label class="text-secondary">{{ $t("meetingConfig") }}</label>
            <fieldset readonly>
              <div v-if="isBBB">
                <read-only-check-box :checked="meeting.onlyShowCameraForOperator" />
                {{ $t("onlyShowCameraForOperator") }}
              </div>
              <!-- <div v-if="isBBB">
                <read-only-check-box :checked="meeting.enableMeetingRecording" />
                {{ $t("enableToggleMeetingRecording") }}
              </div> -->
              <div>
                <read-only-check-box :checked="meeting.enableAutomaticMeetingRecording" />
                {{ $t("enableAutomaticMeetingRecording") }}
              </div>
              <div v-if="isBBB">
                <read-only-check-box :checked="meeting.enableMuteOnStart" />
                {{ $t("meetingMuteOnStart") }}
              </div>
              <div>
                <read-only-check-box :checked="meeting.enableCamera" />
                {{ $t("meetingShareCamera") }}
              </div>
              <div v-if="isBBB">
                <read-only-check-box :checked="meeting.enableMicro" />
                {{ $t("enableMicro") }}
              </div>
              <!-- <div v-if="isBBB">
                <read-only-check-box :checked="meeting.enablePrivateChat" />
                {{ $t("meetingPrivateChatAllow") }}
              </div>
              <div v-if="isBBB">
                <read-only-check-box :checked="meeting.enablePublicChat" />
                {{ $t("meetingPublicChatAllow") }}
              </div>
              <div v-if="isBBB">
                <read-only-check-box :checked="meeting.enableTakingNote" />
                {{ $t("enableTakingNote") }}
              </div> -->
              <div v-if="isZoom">
                <read-only-check-box :checked="meeting.enableParticipationBeforeHost" />
                {{ $t("enableParticipationBeforeHost") }}
              </div>
<!--              <div v-if="isBBB">
                <read-only-check-box :checked="meeting.enableSendAttachmentInChat" />
                Cho phép gửi tập tin trong chat
              </div>-->
            </fieldset>
          </b-col>

          <b-col cols="7" class="pl-5" style="max-height: 530px; overflow: auto;">
            <template v-if="meeting.publicMeeting && meeting.status === 'INPROGRESS'">
              <h5 class="mb-4">{{ $t("meetingParticipantsJoined", { count: meeting.participantsJoined.length }) }}</h5>
              <b-list-group class="mb-4">
                <b-list-group-item v-for="meetingParticipant in meeting.participantsJoined" :key="meetingParticipant.name">
                  {{ meetingParticipant.name }}
                  <b-badge v-if="['OPERATOR', 'OPERATER'].includes(meetingParticipant.role)" variant="info" class="ml-2">
                    {{ $t("meetingOperatorBadge") }}
                  </b-badge>
                </b-list-group-item>
              </b-list-group>
            </template>
            <template v-if="!meeting.publicMeeting && meeting.status === 'INPROGRESS'">
<!--              <h5 class="mb-4">{{ $t("meetingParticipantsJoined", { count: meeting.participantsJoined.length }) }}</h5>-->
              <b-form-radio-group class="mb-2" size="sm" buttons button-variant="outline-primary"
                v-model="inProgressMeetingParticipantsViewMode" :options="inProgressMeetingParticipantsViewModeOptions" />
              <b-list-group class="mb-4">
                <b-list-group-item
                    v-for="meetingParticipant in showParticipantsJoined"
                    :key="meetingParticipant.id || meetingParticipant.name"
                    class="d-flex flex-row">
                  <div class="flex-grow-1">
                    <div>
                      {{ meetingParticipant.name }}
                      <b-badge v-if="['OPERATOR', 'OPERATER'].includes(meetingParticipant.role)" variant="info" class="ml-2">
                        {{ $t("meetingOperatorBadge") }}
                      </b-badge>
                    </div>
                    <b-row v-if="meetingParticipant.email || meetingParticipant.phone">
                      <b-col cols="7" v-if="meetingParticipant.email">
                        <b-icon class="mr-1" icon="envelope"/>
                        {{ meetingParticipant.email }}
                      </b-col>
                      <b-col cols="5" v-if="meetingParticipant.phone">
                        <b-icon class="mr-1" icon="telephone"/>
                        {{ meetingParticipant.phone }}
                      </b-col>
                    </b-row>
                  </div>
                  <div class="align-self-center">
                    <b-dropdown v-if="meetingParticipant.id && canChangeParticipants" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
                      <template v-slot:button-content>
                        <b-icon-three-dots-vertical />
                      </template>
                      <b-dropdown-item-button @click="onClickSendSingleEmailInvitation(meetingParticipant)">{{ $t("sendEmailInvitation") }}</b-dropdown-item-button>
                      <b-dropdown-item-button v-if="meetingParticipant.phone" @click="onClickSendSingleSmsInvitation(meetingParticipant)">{{ $t("sendSmsInvitation") }}</b-dropdown-item-button>
                      <b-dropdown-item-button @click="onClickUpdateParticipant(meetingParticipant)">{{ $t("update") }}</b-dropdown-item-button>
                      <b-dropdown-item-button v-if="meetingParticipant.privateJoinUrl" @click="onClickCopyPrivateJoinUrl(meetingParticipant)">{{ $t("copyJoinUrl") }}</b-dropdown-item-button>
                    </b-dropdown>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </template>

            <div class="d-flex flex-row align-items-center justify-content-between mb-4">
              <h5 class="m-0">{{ $t("meetingParticipantsList") }} ({{ peers.length ? peers.length : meeting.meetingParticipants.length }})</h5>
              <div>
                <!--<b-button v-if="meeting.status !== 'END'" class="mr-3" variant="outline-primary" size="sm" @click="onClickSendEmailInvitation">
                  <b-icon-envelope class="mr-1" />
                  Gửi email mời tham gia
                </b-button>-->
<!--                <b-button v-if="shouldShowSendAllSmsInvitationBtn" class="mr-3" variant="outline-primary" size="sm" @click="onClickSendAllSmsInvitation">
                  <b-icon-envelope class="mr-1" />
                  Gửi SMS mời tham gia
                </b-button>-->
                <b-button v-if="canAddParticipants" variant="outline-primary" size="sm" v-b-modal:new-participant-modal>
                  <b-icon-plus /> {{ $t("addMeetingParticipant") }}
                </b-button>
              </div>
            </div>
            <b-list-group >
              <div v-if="!peers.length">
                <b-list-group-item v-for="meetingParticipant in meeting.meetingParticipants" :key="meetingParticipant.id" class="d-flex flex-row">
                  <div class="flex-grow-1">
                    <div>
                      {{ meetingParticipant.name }}
                      <b-badge v-if="meetingParticipant.role === 'OPERATOR'" variant="info" class="ml-2">{{ $t("meetingOperatorBadge") }}</b-badge>
                    </div>
                    <b-row>
                      <b-col cols="7">
                        <b-icon class="mr-1" icon="envelope"/>
                        {{ meetingParticipant.email }}
                      </b-col>
                      <b-col cols="5" v-if="meetingParticipant.phone">
                        <b-icon class="mr-1" icon="telephone"/>
                        {{ meetingParticipant.phone }}
                      </b-col>
                    </b-row>
                  </div>
                  <div class="align-self-center">
                    <b-dropdown v-if="canChangeParticipants" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
                      <template v-slot:button-content>
                        <b-icon-three-dots-vertical />
                      </template>
                      <b-dropdown-item-button @click="onClickSendSingleEmailInvitation(meetingParticipant)">{{ $t("sendEmailInvitation") }}</b-dropdown-item-button>
                      <b-dropdown-item-button v-if="meetingParticipant.phone" @click="onClickSendSingleSmsInvitation(meetingParticipant)">{{ $t("sendSmsInvitation") }}</b-dropdown-item-button>
                      <b-dropdown-item-button @click="onClickUpdateParticipant(meetingParticipant)">{{ $t("update") }}</b-dropdown-item-button>
                      <b-dropdown-item-button v-if="meetingParticipant.privateJoinUrl" @click="onClickCopyPrivateJoinUrl(meetingParticipant)">{{ $t("copyJoinUrl") }}</b-dropdown-item-button>
                    </b-dropdown>
                  </div>
                </b-list-group-item>
              </div>
              <div v-if="peers.length">
                <b-list-group-item v-for="peer in peers" :key="peer.uid" class="d-flex flex-row">
                  <div class="flex-grow-1">
                    <div>
                      {{ peer.uname }}
          
                    </div>
                  </div>
                  <div>
                      {{ formatDate(peer.logDate) }}
          
                    </div>
                </b-list-group-item>
              </div>
              
            </b-list-group>
          </b-col>
        </b-row>
      </template>

    </b-container>

    <b-modal id="new-participant-modal" :title="$t('addMeetingParticipant')" hide-footer no-close-on-backdrop>
      <NewMeetingParticipantModal modal-id="new-participant-modal" :meeting-id="meetingId" @submitted="fetchMeeting" />
    </b-modal>

    <b-modal id="update-participant-modal" :title="$t('updateMeetingParticipant')" hide-footer no-close-on-backdrop>
      <UpdateMeetingParticipantModal modal-id="update-participant-modal" :meeting-id="meetingId" :meeting-participant="participantToUpdate" @submitted="fetchMeeting" />
    </b-modal>

    <b-modal id="send-meeting-email-confirm-modal" size="lg" :title="emailStatusActionModalQuestionText" hide-footer no-close-on-backdrop>
      <send-meeting-email-confirm-modal modal-id="send-meeting-email-confirm-modal"
        :meeting-id="meetingId" :status="emailStatus" @submitted="fetchMeeting" />
    </b-modal>
    <b-modal id="qr-code-modal" header-class="header-qr" hide-footer :title="$t('QRCodeText')">
      <div  style="display: flex; justify-content: center ; margin-bottom: 2em;">  
        <qr-code ref="qrContent" class="qr-code" :size="qrsize" :text="participantJoinUrl"></qr-code>
      </div>  
      <b-form-input style="margin-bottom: 2em;" id="input-join-url" size="sm" :value="participantJoinUrl" readonly/>
      <div class="qr-button">
        <b-button size="sm" style="margin: 0.25rem" variant="outline-primary" @click="copiedLinkQR()">
          <b-icon-files class="mr-1" /> {{ $t("copieQRLink") }}
      </b-button>
      <b-button size="sm" style="margin: 0.25rem" variant="primary" @click="downloadQR()">
        <b-icon-download class="mr-1" /> {{ $t("download") }}
      </b-button>
      </div>
    </b-modal>
  </main>
</template>


<style lang="scss" scoped>
  #conf-info-2 {
    line-height: 160%;
  }
  .qr-code {
    width: 250px;
    height: 250px;
    border: 1px solid;
    padding: 1.5em;
    border-radius: 1em;
    display: flex;
  }
  .qr-button {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
  }
  .header-qr {
    border: none;
  }
</style>


<script lang="ts">
  import {Component, Vue, Watch, Ref} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";

  import * as meetingService from "@/service/meetingService";
  import * as util from "@/service/util";
  import ReadOnlyCheckBox from "@/components/ReadOnlyCheckBox.vue";
  import NewMeetingParticipantModal from "@/components/meeting/NewMeetingParticipantModal.vue";
  import UpdateMeetingParticipantModal from "@/components/meeting/UpdateMeetingParticipantModal.vue";
  import SendMeetingEmailConfirmModal from "@/components/meeting/SendMeetingEmailConfirmModal.vue";


  @Component({
    components: {
      NewMeetingParticipantModal,
      UpdateMeetingParticipantModal,
      ReadOnlyCheckBox,
      SendMeetingEmailConfirmModal,
    }
  })
  export default class MeetingDetails extends BaseComponent {

    readonly statusText = {
      PENDING : this.$t("meetingStatusPending").toString(),
      INPROGRESS : this.$t("meetingStatusInProgress").toString(),
      END : this.$t("meetingStatusEnded").toString(),
      CANCEL : this.$t("meetingStatusCancelled").toString(),
    };

    readonly statusBadgeVariant = {
      PENDING : "warning",
      INPROGRESS : "success",
      END : "secondary",
      CANCEL : "danger"
    };

    meetingId: number = null;

    isLoading = false;
    meeting = null;
    participantJoinUrl = '';
    qrsize: Number = 500;
    emailStatus = "";
    emailStatusAlertVariant = "info";
    emailStatusAlertText = "";
    emailStatusButtonText = "";
    emailStatusActionModalQuestionText = "";
    emailStatusActionSuccessText = "";

    inProgressMeetingParticipantsViewMode: "joined" | "notJoined" = "joined";
    inProgressMeetingParticipantsViewModeOptions = [
      { value: "joined", text: this.$t("meetingParticipantsJoined") },
      { value: "notJoined", text: this.$t("meetingParticipantsNotJoined") },
    ];
    @Ref('qrContent') qrContent!: any;
    get showParticipantsJoined() {
      return this.inProgressMeetingParticipantsViewMode === "joined"? this.meeting.participantsJoined : this.meeting.participantsNotJoined;
    }

    participantToUpdate = null;

    isLoadingRecordUrl = false;
    playbackUrl: string = null;
    peers = [];
    contentRoom: string = null;
    mounted() {
      this.meetingId = +this.$route.params.id || null;
      this.fetchMeeting();
    }

    formatDate(date) {
      return util.localDateTimeFormat(date);
    }

    async copiedLinkQR() {
      await navigator.clipboard.writeText(this.participantJoinUrl);
      this.toastSuccessDelay(this.$t("copied").toString());
    }

    downloadQR() {
      if(this.qrContent) {
        const url =  this.qrContent.$el.lastChild.src;
        const a = document.createElement('a')
        a.href = url
        a.download = "QRcode_" + this.meeting.name;
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        
      }
    }

    async fetchMeeting() {
      try {
        this.isLoading = true;
        this.meeting = await meetingService.getDetails(this.meetingId);
        if (this.meeting.participantsJoined) {
          const notJoinedCount = util.countPredicate(
            this.meeting.meetingParticipants,
            m => this.meeting.participantsJoined.every(pj => pj.name !== m.name)
          );

          this.inProgressMeetingParticipantsViewModeOptions = [
            { value: "joined", text: this.$t("meetingParticipantsJoined", { count: this.meeting.participantsJoined.length }) },
            { value: "notJoined", text: this.$t("meetingParticipantsNotJoined", { count: notJoinedCount }) },
          ];
        }

        this.isLoading = false;

        if (this.meeting.status === "END" && (this.meeting.owner || this.isUserAdmin())) {
          this.isLoadingRecordUrl = true;
          this.playbackUrl = (await meetingService.getMeetingRecording(this.meetingId)).playbackUrl;
          this.isLoadingRecordUrl = false;
          this.peers = await meetingService.getParticipantMeeting({mid: this.meeting.internalMeetingId});
          this.contentRoom = await meetingService.getContentMeeting({mid: this.meeting.internalMeetingId});
        }
        if(this.meeting.participantJoinUrl){
          this.meeting.participantJoinUrl = this.formatUrl(this.meeting.participantJoinUrl);
          this.participantJoinUrl = this.meeting.participantJoinUrl;
        }
        if(this.meeting.hostJoinUrl){
          this.meeting.hostJoinUrl = this.formatUrl(this.meeting.hostJoinUrl);
        }
      } catch (e) {
        console.error(e);
        this.toastError(e);

      } finally {
        this.isLoading = false;
        this.isLoadingRecordUrl = false;
      }
    }

    @Watch("meeting", { immediate: true, deep: true })
    onMeetingChange(meeting: any) {
      // Reset
      this.emailStatus = "";
      this.emailStatusAlertVariant = "info";
      this.emailStatusAlertText = "";
      this.emailStatusButtonText = "";
      this.emailStatusActionModalQuestionText = "";
      this.emailStatusActionSuccessText = "";

      if (!meeting) return;

      if (["PENDING", "INPROGRESS"].includes(meeting.status)) {
        if (meeting.invitationSent >= 0) {
          if (meeting.invitationSent === 0) {
            this.emailStatus = "NEW";
            this.emailStatusAlertVariant = "warning";
            this.emailStatusAlertText = this.$t("notSendEmailInvitation").toString();
            this.emailStatusButtonText = this.$t("sendEmailInvitation2").toString();
            this.emailStatusActionModalQuestionText = (meeting.publicMeeting ?
              this.$t("sendEmailInvitation2").toString() :
              this.$t("sendEmailInvitationToAll").toString()
            ) + "?";
            this.emailStatusActionSuccessText = this.$t("sendEmailInvitationSuccessful").toString();
          } else {
            this.emailStatusAlertText = this.$t("alreadySentEmailInvitation").toString();
          }
        }
        if (meeting.updateSent >= 0) {
          if (meeting.updateSent === 0) {
            this.emailStatus = "UPDATE";
            this.emailStatusAlertVariant = "warning";
            this.emailStatusAlertText = this.$t("notSendMeetingUpdateEmail").toString();
            this.emailStatusButtonText = this.$t("sendMeetingUpdateEmail").toString();
            this.emailStatusActionModalQuestionText = (meeting.publicMeeting ?
              this.$t("sendMeetingUpdateEmail").toString() :
              this.$t("sendMeetingUpdateEmailToAll").toString()
            ) + "?";
            this.emailStatusActionSuccessText = this.$t("sendMeetingUpdateEmailSuccessful").toString();
          } else {
            this.emailStatusAlertText = this.$t("alreadySentMeetingUpdateEmail").toString();
          }
        }
      }

      if (meeting.cancellationSent >= 0 && meeting.status === "CANCEL") {
        if (meeting.cancellationSent === 0) {
          this.emailStatus = "CANCEL";
          this.emailStatusAlertVariant = "warning";
          this.emailStatusAlertText = this.$t("notSendMeetingCancelEmail").toString();
          this.emailStatusButtonText = this.$t("sendMeetingCancelEmail").toString();
          this.emailStatusActionModalQuestionText = (meeting.publicMeeting ?
            this.$t("sendMeetingCancelEmail").toString() :
            this.$t("sendMeetingCancelEmailToAll").toString()
          ) + "?";
          this.emailStatusActionSuccessText = this.$t("sendMeetingCancelEmailSuccessful").toString();
        } else {
          this.emailStatusAlertText = this.$t("alreadySentMeetingCancelEmail").toString();
        }
      }
    }

    get isBBB() {
      return this.meeting?.corePlatform?.toLowerCase() === "bbb";
    }

    get isZoom() {
      return this.meeting?.corePlatform?.toLowerCase() === "zoom";
    }

    get actualTimeStr(): string {
      let ret = "-";
      if (this.meeting?.actualStartTime) {
        ret = this.localTimeFormat(this.meeting.actualStartTime);
        if (this.meeting.actualEndTime) {
          ret = this.localTimeFormat(this.meeting.actualStartTime) + " - " + this.localTimeFormat(this.meeting.actualEndTime);
        }
      }
      return ret;
    }

    get participantJoinUrlHasAccessCode() {
      return this.meeting?.participantJoinUrl?.includes("?accessCode=");
    }

    get operators(): any[] {
      return this.meeting?.meetingParticipants?.filter(it => it.role === "OPERATOR") || [];
    }

    get participants(): any[] {
      return this.meeting?.meetingParticipants?.filter(it => it.role !== "OPERATOR") || [];
    }

    get shouldShowSendAllSmsInvitationBtn(): boolean {
      return this.meeting && this.meeting.status !== "END" && this.meeting.meetingParticipants.some(p => p.phone);
    }

    get canAddParticipants(): boolean {
      return !['END', 'CANCEL'].includes(this.meeting.status) && !this.meeting.publicMeeting && this.meeting.owner;
    }

    get canChangeParticipants(): boolean {
      return this.meeting.owner && !['END', 'CANCEL'].includes(this.meeting.status);
    }


    async onClickStartMeeting() {
      const confirm = await this.$bvModal.msgBoxConfirm(this.$t("startMeetingWarning").toString(), {
        okVariant: 'primary',
        okTitle: this.$t("ok").toString(),
        cancelTitle: this.$t("cancel").toString(),
      });
      if (!confirm) return;

      try {
        this.showLoading();
        const res = await meetingService.startMeeting(this.meetingId);
        this.fetchMeeting();
        open(res.startUrl);

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }

    async onClickJoinMeeting() {
      try {
        this.showLoading();
        const res = await meetingService.joinMeeting(this.meetingId);
        this.fetchMeeting();
        open(res.joinUrl);

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }

    async onClickEndMeeting() {
      const modalMessage = this.$t("endThisMeetingWarning").toString();
      const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirm) {
        try {
          this.showLoading();
          await meetingService.endMeeting(this.meetingId);
          this.fetchMeeting();

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }


    async onClickCopyHostJoinUrl() {
      await navigator.clipboard.writeText(this.meeting.hostJoinUrl);
      this.toastSuccess(this.$t("alert").toString(), this.$t("copiedHostJoinUrl").toString());
    }


    async onClickCopyJoinUrl() {
      await navigator.clipboard.writeText(this.meeting.participantJoinUrl);
      this.toastSuccess(this.$t("alert").toString(), this.$t("copied").toString());
    }

    async onClickCopyJoinUrlAndAccessCode() {
      await navigator.clipboard.writeText(`${this.meeting.participantJoinUrl}\n${this.$t("accessCode")}: ${this.meeting.accessCode}`);
      this.toastSuccess(this.$t("alert").toString(), this.$t("copied").toString());
    }

    async onClickEmailStatusAlertButton() {
      this.$bvModal.show("send-meeting-email-confirm-modal");
    }

    async onClickOpenQRCodeModal() {
      this.$bvModal.show("qr-code-modal");
    }


    async onClickSendAllEmailInvitation() {
      const modalMessage = this.$t("sendEmailInvitationToAll").toString() + "?";
      const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'primary',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirm) {
        try {
          this.showLoading();
          await meetingService.sendInvitationEmails(this.meetingId);
          this.toastSuccessDelay(this.$t("sendEmailInvitationSuccessful").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }


    async onClickSendSingleEmailInvitation(meetingParticipant) {
      const modalMessage = this.$t("sendEmailInvitationConfirm2", { email: meetingParticipant.email }).toString();
      const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'primary',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirm) {
        try {
          this.showLoading();
          await meetingService.sendSingleInvitationEmail(this.meetingId, meetingParticipant.id);
          this.toastSuccessDelay(this.$t("sendEmailInvitationSuccessful").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }


    /*async onClickSendAllSmsInvitation() {
      const modalMessage = this.meeting.publicMeeting? "Gửi SMS mời tham gia?" : "Gửi SMS mời đến tất cả những người tham gia?";
      const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'primary',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirm) {
        try {
          this.showLoading();
          await meetingService.sendInvitationSms(this.meetingId, 0);
          this.toastSuccessDelay(
            this.meeting.publicMeeting? "Đã gửi SMS mời tham gia" : "Đã gửi SMS mời đến tất cả những người tham gia"
          );

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }*/

    async onClickSendSingleSmsInvitation(meetingParticipant) {
      const modalMessage = this.$t("sendSmsInvitationConfirm", { phone: meetingParticipant.phone }).toString();
      const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'primary',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirm) {
        try {
          this.showLoading();
          await meetingService.sendInvitationSms(this.meetingId, meetingParticipant.id);
          this.toastSuccessDelay(this.$t("sendSmsInvitationSuccessful", { phone: meetingParticipant.phone }).toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

    onClickUpdateParticipant(meetingParticipant) {
      this.participantToUpdate = meetingParticipant;
      this.$bvModal.show("update-participant-modal");
    }

    async onClickCopyPrivateJoinUrl(p) {
      await navigator.clipboard.writeText(this.formatUrl(p.privateJoinUrl));
      this.toastSuccessDelay(this.$t("copied").toString());
    }

    async downloadFileParticipant() {
      const sheetData = this.peers.map(peer => [
          peer.uname,
          this.formatDate(peer.logDate)
        ]);
        sheetData.unshift(["NGƯỜI THAM GIA", "THỜI GIAN THAM DỰ"]);

        const XLSX = await import("xlsx");

        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet);
        XLSX.writeFile(workbook, "participant.xlsx");
    }

    async downloadFileContentRoom() {
      console.log(this.contentRoom);
      const blob = new Blob([this.contentRoom], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "contentRoom.txt";

      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    }

    async onClickCancelMeeting() {
      const modalMessage = this.$t("cancelThisMeetingConfirm").toString();
      const confirmCancel = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmCancel) {
        try {
          this.showLoading();
          await meetingService.cancelMeeting(this.meetingId);
          this.toastSuccessDelay(this.$t("cancelMeetingSuccessful").toString());
          this.fetchMeeting();

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }


    async onClickDeleteMeeting() {
      const modalMessage = this.$t("deleteThisMeetingConfirm").toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await meetingService.deleteMeeting(this.meetingId);
          this.hideLoading();

          this.$router.replace("/meetings");
          this.toastSuccessDelay(this.$t("deleteMeetingSuccessful").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

  }

</script>
